import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import OffseasonTimeline from "./OffseasonTimeline";
import "./Dashboard.css";
import "./variables.css";
import API_URL from "./config";
import useTeamTheme from "./useTeamTheme";
import { getTeamTheme } from "./teamTheme";

// Same OVR formula used in PlayGame.js
const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerOverall = (p) => {
  const offense = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq]);
  const defense = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping]);
  const athletic = avg([p.speed, p.stamina, p.injury, p.jumping]);
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const hasInitialized = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeSaveId, setActiveSaveId] = useState(null);
  const [saveName, setSaveName] = useState("");
  const [currentSeason, setCurrentSeason] = useState("");
  const [teamId, setTeamId] = useState(null);
  const [team, setTeam] = useState(null);
  const [topPlayers, setTopPlayers] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [allUpcomingGames, setAllUpcomingGames] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [allPlayedGames, setAllPlayedGames] = useState([]);
  const [conferenceStandings, setConferenceStandings] = useState([]);
  const [allStandingsMap, setAllStandingsMap] = useState({});
  const [lifecycle, setLifecycle] = useState(null);
  const [advancingPhase, setAdvancingPhase] = useState(false);
  const [playoffData, setPlayoffData] = useState(null);
  const [simulatingPlayoffs, setSimulatingPlayoffs] = useState(false);
  const [leagueLeaders, setLeagueLeaders] = useState({ points: [], assists: [], rebounds: [] });
  const [teamLeaders, setTeamLeaders] = useState({ points: [], assists: [], rebounds: [] });

  useTeamTheme(team);

  useEffect(() => {
    // Prevent duplicate fetch in React 18 StrictMode (runs effect twice in dev)
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const saveId = localStorage.getItem("activeSaveId");
        if (!saveId) { setActiveSaveId(null); setLoading(false); return; }
        setActiveSaveId(saveId);

        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        const saveRes = await fetch(`${API_URL}/api/saves/${saveId}`, {
          headers: { "X-User-ID": userId },
        });
        if (!saveRes.ok) { 
          // Clear the bad save ID and show welcome screen instead of error
          localStorage.removeItem("activeSaveId");
          setActiveSaveId(null);
          setLoading(false);
          return;
        }
        const saveData = await saveRes.json();
        const save = saveData.save;
        const tid = save.teamId;
        setTeamId(tid);
        setSaveName(save.name || "");
        setCurrentSeason(save.currentSeason || "");

        const [rosterData, scheduleData, standingsData, lifecycleData, playoffsData] = await Promise.all([
          fetch(`${API_URL}/api/teams/${tid}/roster?save_id=${saveId}`).then((r) => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/teams/${tid}/schedule?save_id=${saveId}`).then((r) => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/standings?save_id=${saveId}`).then((r) => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/saves/${saveId}/lifecycle`, { headers: { "X-User-ID": userId } }).then((r) => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/saves/${saveId}/playoffs`, { headers: { "X-User-ID": userId } }).then((r) => r.ok ? r.json() : null),
        ]);

        const teamObj = rosterData?.team || null;
        setTeam(teamObj);

        const roster = rosterData?.roster || [];
        setTopPlayers([...roster].sort((a, b) => playerOverall(b) - playerOverall(a)).slice(0, 5));

        if (scheduleData) {
          const games = scheduleData.games || [];
          const upcoming = games.filter((g) => !g.played);
          const played = games.filter((g) => g.played);
          setUpcomingGames(upcoming.slice(0, 3));
          setAllUpcomingGames(upcoming);
          setRecentResults(played.slice(-3).reverse());
          setAllPlayedGames(played);
        }

        if (standingsData) {
          const allStandings = standingsData.standings ?? standingsData;
          const conf = teamObj?.conference;
          const confTeams = (conf && allStandings[conf]) ? allStandings[conf] : Object.values(allStandings)[0] || [];
          setConferenceStandings(confTeams.slice(0, 5));

          const map = {};
          Object.entries(allStandings).forEach(([confName, teams]) => {
            (teams || []).forEach((t, idx) => {
              map[t.name] = { wins: t.wins || 0, losses: t.losses || 0, rank: idx + 1, conference: confName };
            });
          });
          setAllStandingsMap(map);
        }

        setLifecycle(lifecycleData || null);
        setPlayoffData(playoffsData || null);

        // Fetch all league/team leaders in a single batch call (much faster!)
        const season = save.currentSeason || 1;
        const leadersRes = await fetch(`${API_URL}/api/stats/dashboard-leaders?save_id=${saveId}&season=${season}&team_id=${tid}`);
        if (leadersRes.ok) {
          const leadersData = await leadersRes.json();
          setLeagueLeaders({
            points: leadersData.leagueLeaders?.points || [],
            assists: leadersData.leagueLeaders?.assists || [],
            rebounds: leadersData.leagueLeaders?.rebounds || [],
          });
          setTeamLeaders({
            points: leadersData.teamLeaders?.points || [],
            assists: leadersData.teamLeaders?.assists || [],
            rebounds: leadersData.teamLeaders?.rebounds || [],
          });
        }
      } catch (err) {
        setError("Error loading dashboard: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Refetch standings when page regains focus (e.g., returning from a game)
  useEffect(() => {
    const handleFocus = async () => {
      const saveId = localStorage.getItem("activeSaveId");
      if (!saveId) return;

      try {
        const res = await fetch(`${API_URL}/api/standings?save_id=${saveId}`);
        if (res.ok) {
          const standingsData = await res.json();
          const allStandings = standingsData.standings ?? standingsData;
          const conf = team?.conference;
          const confTeams = (conf && allStandings[conf]) ? allStandings[conf] : Object.values(allStandings)[0] || [];
          setConferenceStandings(confTeams.slice(0, 5));

          const map = {};
          Object.entries(allStandings).forEach(([confName, teams]) => {
            (teams || []).forEach((t, idx) => {
              map[t.name] = { wins: t.wins || 0, losses: t.losses || 0, rank: idx + 1, conference: confName };
            });
          });
          setAllStandingsMap(map);
        }
      } catch (err) {
        // Failed to refetch standings
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [team]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const winPct = (wins, losses) => {
    const total = (wins || 0) + (losses || 0);
    if (total === 0) return ".000";
    return "." + Math.round(((wins || 0) / total) * 1000).toString().padStart(3, "0");
  };

  const calcStreak = (played, tid) => {
    if (!played.length) return null;
    let count = 0;
    let type = null;
    for (let i = played.length - 1; i >= 0; i--) {
      const g = played[i];
      const isHome = g.homeTeam === tid;
      const myScore = isHome ? g.homeScore : g.awayScore;
      const oppScore = isHome ? g.awayScore : g.homeScore;
      const won = myScore > oppScore;
      if (type === null) type = won ? "W" : "L";
      if ((won && type === "W") || (!won && type === "L")) count++;
      else break;
    }
    return type && count > 0 ? { type, count } : null;
  };

  const confRank = () => {
    if (!team || !conferenceStandings.length) return null;
    const idx = conferenceStandings.findIndex((t) => t.name === team.name);
    return idx >= 0 ? idx + 1 : null;
  };

  const gamesBack = () => {
    if (!team || conferenceStandings.length < 2) return null;
    const first = conferenceStandings[0];
    const my = conferenceStandings.find((t) => t.name === team.name);
    if (!my || first.name === my.name) return null;
    const gb = ((first.wins - my.wins) + (my.losses - first.losses)) / 2;
    return gb > 0 ? gb : null;
  };

  // Home / Away split from all played games
  const homeAwayRecord = (tid) => {
    const home = allPlayedGames.filter((g) => g.homeTeam === tid);
    const away = allPlayedGames.filter((g) => g.homeTeam !== tid);
    const homeW = home.filter((g) => g.homeScore > g.awayScore).length;
    const awayW = away.filter((g) => g.awayScore > g.homeScore).length;
    return {
      home: { w: homeW, l: home.length - homeW },
      away: { w: awayW, l: away.length - awayW },
    };
  };

  // Strength of remaining schedule — avg opponent win % across all unplayed games
  const calcSOS = () => {
    const opps = allUpcomingGames
      .map((g) => {
        const isHome = g.homeTeam === teamId;
        const name = isHome ? g.awayTeamName : g.homeTeamName;
        return allStandingsMap[name];
      })
      .filter(Boolean);
    if (!opps.length) return null;
    const avgPct = opps.reduce((sum, o) => {
      const total = o.wins + o.losses;
      return sum + (total > 0 ? o.wins / total : 0.5);
    }, 0) / opps.length;
    // Hardest remaining opponent by win%
    const hardest = opps.reduce((best, o) => {
      const pct = o.wins + o.losses > 0 ? o.wins / (o.wins + o.losses) : 0;
      const bestPct = best.wins + best.losses > 0 ? best.wins / (best.wins + best.losses) : 0;
      return pct > bestPct ? o : best;
    }, opps[0]);
    const hardestGame = allUpcomingGames.find((g) => {
      const isHome = g.homeTeam === teamId;
      const name = isHome ? g.awayTeamName : g.homeTeamName;
      return allStandingsMap[name] === hardest;
    });
    const hardestName = hardestGame
      ? (hardestGame.homeTeam === teamId ? hardestGame.awayTeamName : hardestGame.homeTeamName)
      : null;
    return { avgPct, gamesRemaining: opps.length, hardestName, hardest };
  };

  const getLeaderStat = (player, stat) => {
    const value = player?.stats?.[stat]?.avg;
    return value != null ? parseFloat(value).toFixed(1) : "0.0";
  };

  const phaseLabel = (phase) => {
    const labels = {
      preseason: "Preseason",
      regular_season: "Regular Season",
      play_in: "Play-In",
      playoffs: "Playoffs",
      offseason: "Offseason",
      next_season_setup: "Next Season Setup",
    };
    return labels[phase] || "Season Flow";
  };

  const handleAdvancePhase = async () => {
    try {
      setAdvancingPhase(true);
      const saveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;
      const res = await fetch(`${API_URL}/api/saves/${saveId}/lifecycle/advance`, {
        method: "POST",
        headers: { "X-User-ID": userId },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Unable to advance to playoffs");
      }
      window.location.reload();
    } catch (e) {
      setError(e.message || "Unable to advance to playoffs");
    } finally {
      setAdvancingPhase(false);
    }
  };

  const refreshPlayoffSnapshot = async (saveId, userId, tid) => {
    const [lifecycleData, playoffsData, scheduleData] = await Promise.all([
      fetch(`${API_URL}/api/saves/${saveId}/lifecycle`, { headers: { "X-User-ID": userId } }).then((r) => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/saves/${saveId}/playoffs`, { headers: { "X-User-ID": userId } }).then((r) => r.ok ? r.json() : null),
      tid ? fetch(`${API_URL}/api/teams/${tid}/schedule?save_id=${saveId}`).then((r) => r.ok ? r.json() : null) : Promise.resolve(null),
    ]);

    if (lifecycleData) {
      setLifecycle(lifecycleData);
    }
    if (playoffsData) setPlayoffData(playoffsData);
    if (scheduleData) {
      const games = scheduleData.games || [];
      const upcoming = games.filter((g) => !g.played);
      const played = games.filter((g) => g.played);
      setUpcomingGames(upcoming.slice(0, 3));
      setAllUpcomingGames(upcoming);
      setRecentResults(played.slice(-3).reverse());
      setAllPlayedGames(played);
    }
  };

  const handleSimulateEntirePlayoffs = async () => {
    try {
      setSimulatingPlayoffs(true);
      const saveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;
      const tid = teamId;

      let hasMoreGames = true;

      while (hasMoreGames) {
        const res = await fetch(`${API_URL}/api/saves/${saveId}/playoffs/simulate-next-game`, {
          method: "POST",
          headers: { "X-User-ID": userId },
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.detail || "Unable to simulate playoff games");
        }

        const data = await res.json();

        // Refresh UI after each batch
        await refreshPlayoffSnapshot(saveId, userId, tid);

        // Stop if no more games remain
        if (data.remainingPlayoffGames === 0 || !data.simulatedGames || data.simulatedGames.length === 0) {
          hasMoreGames = false;
        }
      }

      // Final refresh to ensure lifecycle state is updated with offseason phase
      await refreshPlayoffSnapshot(saveId, userId, tid);
    } catch (e) {
      setError(e.message || "Unable to simulate playoffs");
    } finally {
      setSimulatingPlayoffs(false);
    }
  };

  const handleEndPlayoffs = async () => {
    try {
      const saveId = localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      const res = await fetch(`${API_URL}/api/saves/${saveId}/lifecycle/end-playoffs`, {
        method: "POST",
        headers: { "X-User-ID": userId },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Unable to end playoffs");
      }

      // Reload the page to reflect the phase change
      window.location.reload();
    } catch (e) {
      setError(e.message || "Unable to end playoffs");
    }
  };

  const playoffChampionTeamId = playoffData?.playoffChampionTeamId || null;
  const hasPlayoffChampion = Boolean(playoffChampionTeamId);

  // ── State A: No active save ──────────────────────────────────────────────
  if (!loading && !activeSaveId) {
    return (
      <>
        <Navbar />
        <div className="dashboard-welcome-bg">
          <div className="dashboard-welcome-card">
            <div className="welcome-banner" />
            <div className="welcome-body">
              <div className="welcome-logo">🏀</div>
              <h1 className="welcome-headline">Welcome to Courtside</h1>
              <p className="welcome-tagline">
                Build your dynasty. Manage your roster, run practices, and lead your
                team through every game of the season.
              </p>
              <div className="welcome-ctas">
                <Link to="/saves/new" className="cta-btn cta-primary">Create New Save</Link>
                <Link to="/saves" className="cta-btn cta-secondary">Load Existing Save</Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="dashboard-loading">
          <div className="spinner" />
          <p>Loading dashboard…</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="dashboard-loading">
          <p className="dashboard-error">{error}</p>
        </div>
      </>
    );
  }

  // ── State B: Active save ─────────────────────────────────────────────────
  const wins = team?.wins || 0;
  const losses = team?.losses || 0;
  const played = wins + losses;
  const teamInitial = (team?.name || "?")[0].toUpperCase();
  const streak = calcStreak(allPlayedGames, teamId);
  const rank = confRank();
  const gb = gamesBack();
  const splitRecord = homeAwayRecord(teamId);
  const sos = calcSOS();
  const playoffRounds = playoffData?.rounds || [];

  const isEastConf = (name) => String(name || "").toLowerCase().includes("east");
  const isWestConf = (name) => String(name || "").toLowerCase().includes("west");

  const getSeriesForSide = (round, side) => {
    if (!round || !round.conferences) return [];
    return Object.entries(round.conferences)
      .filter(([confName]) => (side === "east" ? isEastConf(confName) : isWestConf(confName)))
      .flatMap(([, list]) => list || []);
  };

  const getFinalsSeries = () => {
    const finalsRound = playoffRounds.find((r) => Number(r.round) === 4);
    if (!finalsRound || !finalsRound.conferences) return [];
    return Object.entries(finalsRound.conferences)
      .filter(([confName]) => {
        const c = String(confName || "").toLowerCase();
        return c === "league" || (!c.includes("east") && !c.includes("west"));
      })
      .flatMap(([, list]) => list || []);
  };

  const eastRounds = playoffRounds
    .filter((r) => Number(r.round) >= 1 && Number(r.round) <= 3)
    .map((r) => ({ ...r, series: getSeriesForSide(r, "east") }))
    .filter((r) => r.series.length > 0);

  const westRounds = playoffRounds
    .filter((r) => Number(r.round) >= 1 && Number(r.round) <= 3)
    .map((r) => ({ ...r, series: getSeriesForSide(r, "west") }))
    .filter((r) => r.series.length > 0);
  const westRoundsMirrored = [...westRounds].reverse();
  const roundDepth = Math.max(eastRounds.length, westRoundsMirrored.length, 1);

  const finalsSeries = getFinalsSeries();

  return (
    <>
      <Navbar />
      <div className="dashboard-page">

        {/* ── Hero Banner ── */}
        <div className="dashboard-hero">
          <div className="hero-inner">
            <div className="hero-left">
              <div className="hero-avatar">{teamInitial}</div>
              <div className="hero-team-info">
                <h1 className="hero-team-name">{team?.name || "—"}</h1>
                <p className="hero-city">{team?.city || ""}</p>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-record">
                <span className="hero-wins">{wins}</span>
                <span className="hero-dash">–</span>
                <span className="hero-losses">{losses}</span>
              </div>
              <p className="hero-pct">{winPct(wins, losses)} Win %</p>
            </div>
          </div>
          <div className="hero-meta">
            {currentSeason && <span>Season {currentSeason}</span>}
            {saveName && <span>Save: "{saveName}"</span>}
            {lifecycle?.currentPhase && (
              <div className="hero-meta-phase">
                <span>Phase: {phaseLabel(lifecycle.currentPhase)}</span>
              </div>
            )}
            {rank && team?.conference && (
              <span className="hero-meta-rank">
                #{rank} in {team.conference}
                {gb && <span className="hero-meta-gb"> · {gb} GB</span>}
              </span>
            )}
            {streak && (
              <span className={`hero-meta-streak ${streak.type === "W" ? "streak-win" : "streak-loss"}`}>
                {streak.type}{streak.count} Streak
              </span>
            )}
          </div>
        </div>

        <div className="dashboard-content">

          {lifecycle?.currentPhase === "offseason" && (
            <>
              <div className="dash-card">
                <div className="dash-card-header">
                  <span className="dash-card-eyebrow">Offseason Phase</span>
                </div>
                <div className="dash-card-body">
                  <OffseasonTimeline 
                    onAdvanceToRegularSeason={handleAdvancePhase}
                    isAdvancing={advancingPhase}
                  />
                </div>
              </div>
            </>
          )}

          {lifecycle?.currentPhase === "playoffs" && playoffRounds.length > 0 && (
            <div className="dash-card playoff-bracket-card">
              <div className="dash-card-header dash-card-header--flex">
                <span className="dash-card-eyebrow">Playoff Bracket</span>
                {lifecycle?.currentPhase === "playoffs" && (
                  <>
                    {(lifecycle?.remainingPlayoffGames || 0) > 0 && (
                      <button
                        className="playoff-simulate-btn"
                        onClick={handleSimulateEntirePlayoffs}
                        disabled={simulatingPlayoffs}
                      >
                        {simulatingPlayoffs ? "Simulating..." : "Simulate Entire Playoffs"}
                      </button>
                    )}
                    {(lifecycle?.remainingPlayoffGames || 0) === 0 && hasPlayoffChampion && (
                      <button
                        className="playoff-simulate-btn"
                        onClick={handleEndPlayoffs}
                        title="Advance to offseason when playoffs are complete"
                      >
                        Advance to Offseason
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="dash-card-body playoff-bracket-body">
                <div className={`playoff-tree-layout depth-${roundDepth}`}>
                  <div className="playoff-tree-side playoff-tree-side--east">
                    <p className="playoff-side-label">East</p>
                    <div
                      className="playoff-side-columns"
                      style={{ gridTemplateColumns: `repeat(${Math.max(eastRounds.length, 1)}, minmax(150px, 170px))` }}
                    >
                      {eastRounds.map((round) => (
                        <div key={`east-${round.round}`} className={`playoff-side-round round-${round.round}`}>
                          <h3 className="playoff-round-title">{round.label || `Round ${round.round}`}</h3>
                          {round.series.map((series) => (
                            <div key={series.seriesId} className="playoff-series-card">
                              <div className="playoff-series-head">
                                <span className="playoff-seed">#{series.higherSeed ?? "-"}</span>
                                <span className="playoff-team-name">{series.higherSeedTeamName || "TBD"}</span>
                                <span className="playoff-series-wins">{series.higherSeedWins ?? 0}</span>
                              </div>
                              <div className="playoff-series-head">
                                <span className="playoff-seed">#{series.lowerSeed ?? "-"}</span>
                                <span className="playoff-team-name">{series.lowerSeedTeamName || "TBD"}</span>
                                <span className="playoff-series-wins">{series.lowerSeedWins ?? 0}</span>
                              </div>
                              <div className="playoff-series-meta">
                                Best of {series.bestOf || 7}
                                {series.winnerTeamName ? ` · Winner: ${series.winnerTeamName}` : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="playoff-tree-finals">
                    <p className="playoff-side-label">Finals</p>
                    <h3 className="playoff-round-title">NBA Finals</h3>
                    {finalsSeries.length === 0 ? (
                      <div className="playoff-finals-empty">Waiting for conference champions</div>
                    ) : (
                      finalsSeries.map((series) => (
                        <div key={series.seriesId} className="playoff-series-card playoff-series-card--finals">
                          <div className="playoff-series-head">
                            <span className="playoff-seed">#{series.higherSeed ?? "-"}</span>
                            <span className="playoff-team-name">{series.higherSeedTeamName || "TBD"}</span>
                            <span className="playoff-series-wins">{series.higherSeedWins ?? 0}</span>
                          </div>
                          <div className="playoff-series-head">
                            <span className="playoff-seed">#{series.lowerSeed ?? "-"}</span>
                            <span className="playoff-team-name">{series.lowerSeedTeamName || "TBD"}</span>
                            <span className="playoff-series-wins">{series.lowerSeedWins ?? 0}</span>
                          </div>
                          <div className="playoff-series-meta">
                            Best of {series.bestOf || 7}
                            {series.winnerTeamName ? ` · Champion: ${series.winnerTeamName}` : ""}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="playoff-tree-side playoff-tree-side--west">
                    <p className="playoff-side-label">West</p>
                    <div
                      className="playoff-side-columns"
                      style={{ gridTemplateColumns: `repeat(${Math.max(westRoundsMirrored.length, 1)}, minmax(150px, 170px))` }}
                    >
                      {westRoundsMirrored.map((round) => (
                        <div key={`west-${round.round}`} className={`playoff-side-round round-${round.round}`}>
                          <h3 className="playoff-round-title">{round.label || `Round ${round.round}`}</h3>
                          {round.series.map((series) => (
                            <div key={series.seriesId} className="playoff-series-card">
                              <div className="playoff-series-head">
                                <span className="playoff-seed">#{series.higherSeed ?? "-"}</span>
                                <span className="playoff-team-name">{series.higherSeedTeamName || "TBD"}</span>
                                <span className="playoff-series-wins">{series.higherSeedWins ?? 0}</span>
                              </div>
                              <div className="playoff-series-head">
                                <span className="playoff-seed">#{series.lowerSeed ?? "-"}</span>
                                <span className="playoff-team-name">{series.lowerSeedTeamName || "TBD"}</span>
                                <span className="playoff-series-wins">{series.lowerSeedWins ?? 0}</span>
                              </div>
                              <div className="playoff-series-meta">
                                Best of {series.bestOf || 7}
                                {series.winnerTeamName ? ` · Winner: ${series.winnerTeamName}` : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Main 2-Column Grid ── */}
          {lifecycle?.currentPhase !== "offseason" && (
          <div className="dashboard-grid">

            {/* Left column */}
            <div className="dashboard-col-left">

              {/* Upcoming Games */}
              {lifecycle?.currentPhase !== "offseason" && (
              <div className="dash-card">
                <div className="dash-card-header">
                  <span className="dash-card-eyebrow">Upcoming Games</span>
                </div>
                <div className="dash-card-body">
                  {upcomingGames.length === 0 ? (
                    <div className="season-complete">
                      <span className="season-complete-icon">🏁</span>
                      <p className="season-complete-text">
                        {lifecycle?.currentPhase === "playoffs"
                          ? hasPlayoffChampion
                            ? "Playoff Phase Complete"
                            : "Playoffs Still In Progress"
                          : "Season Segment Complete"}
                      </p>
                      <p className="season-complete-sub">
                        {lifecycle?.currentPhase === "playoffs" && !hasPlayoffChampion
                          ? "Your team has no remaining games, but the champion has not been decided yet."
                          : "No remaining games in this phase."}
                      </p>
                      {lifecycle?.canAdvance && lifecycle?.currentPhase === "regular_season" && (
                        <button className="play-now-btn" onClick={handleAdvancePhase} disabled={advancingPhase} style={{ marginTop: '15px' }}>
                          {advancingPhase ? "Advancing..." : "🏆 Advance to Playoffs"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="upcoming-list">
                      {upcomingGames.map((g, i) => {
                        const isHome = g.homeTeam === teamId;
                        const opponentName = isHome ? g.awayTeamName : g.homeTeamName;
                        const oppInfo = allStandingsMap[opponentName];
                        const isNext = i === 0;
                        return (
                          <div key={i} className={`upcoming-row ${isNext ? "upcoming-row--next" : ""}`}>
                            <div className="upcoming-row-left">
                              <span className={`badge ${isHome ? "badge-home" : "badge-away"}`}>
                                {isHome ? "HOME" : "AWAY"}
                              </span>
                              <div className="upcoming-opponent-block">
                                <span className="upcoming-opponent">{opponentName || "TBD"}</span>
                                {oppInfo && (
                                  <span className="upcoming-opp-meta">
                                    #{oppInfo.rank} {oppInfo.conference} · {oppInfo.wins}–{oppInfo.losses}
                                  </span>
                                )}
                                {g.gameType === "playoff" && (
                                  <span className="upcoming-opp-meta">
                                    {g.conference ? `${g.conference} · ` : ""}
                                    {g.round ? `Round ${g.round} · ` : ""}
                                    Game {g.seriesGameNumber || 1} of {g.bestOf || 7}
                                  </span>
                                )}
                              </div>
                            </div>
                            {isNext && (
                              <button
                                className="play-now-btn"
                                onClick={() => navigate("/play-game", { state: { game: g } })}
                              >
                                ▶ Play
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Team Record */}
              {lifecycle?.currentPhase !== "offseason" && (
              <div className="dash-card">
                <div className="dash-card-header dash-card-header--flex">
                  <span className="dash-card-eyebrow">Team Record</span>
                  {streak && (
                    <span className={`streak-badge ${streak.type === "W" ? "streak-badge--win" : "streak-badge--loss"}`}>
                      {streak.type}{streak.count}
                    </span>
                  )}
                </div>
                <div className="dash-card-body">
                  <div className="record-row">
                    <div className="record-block">
                      <span className="record-value wins-value">{wins}</span>
                      <span className="record-label">Wins</span>
                    </div>
                    <div className="record-divider" />
                    <div className="record-block">
                      <span className="record-value losses-value">{losses}</span>
                      <span className="record-label">Losses</span>
                    </div>
                    {rank && (
                      <>
                        <div className="record-divider" />
                        <div className="record-block">
                          <span className="record-value record-rank">#{rank}</span>
                          <span className="record-label">{team?.conference ? team.conference.split(" ")[0] : "Conf"}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <p className="record-pct">{winPct(wins, losses)} Win %</p>
                  <div className="record-progress-track">
                    <div className="record-progress-bar" style={{ width: played > 0 ? `${(wins / played) * 100}%` : "0%" }} />
                  </div>
                  <p className="record-progress-label">{played} games played</p>
                </div>
              </div>
              )}

              {/* Top Players */}
              {topPlayers.length > 0 && (
                <div className="dash-card">
                  <div className="dash-card-header">
                    <span className="dash-card-eyebrow">Top Players</span>
                  </div>
                  <div className="top-players-strip">
                    {topPlayers.map((p) => {
                      const ovr = playerOverall(p);
                      const feet = p.height ? Math.floor(p.height / 12) : null;
                      const inches = p.height ? p.height % 12 : null;
                      const heightStr = feet !== null ? `${feet}'${inches}"` : "—";
                      const weightStr = p.weight ? `${p.weight} lbs` : "—";
                      return (
                        <Link key={p._id} to={`/players/${p._id}`} className="player-strip-row">
                          <span className="strip-pos">{p.position}</span>
                          <span className="strip-name">{p.name}</span>
                          <span className="strip-physical">
                            {heightStr} · {weightStr}
                          </span>
                          <span className="strip-ovr">{ovr} OVR</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Home / Away Split */}
              {lifecycle?.currentPhase !== "offseason" && played > 0 && (
                <div className="dash-card">
                  <div className="dash-card-header">
                    <span className="dash-card-eyebrow">Home / Away Split</span>
                  </div>
                  <div className="dash-card-body">
                    <div className="split-grid">
                      <div className="split-block">
                        <span className="split-label">🏠 Home</span>
                        <span className="split-record">
                          <span className="wins-value">{splitRecord.home.w}</span>
                          <span className="split-dash">–</span>
                          <span className="losses-value">{splitRecord.home.l}</span>
                        </span>
                        <span className="split-pct">{winPct(splitRecord.home.w, splitRecord.home.l)}</span>
                      </div>
                      <div className="split-divider" />
                      <div className="split-block">
                        <span className="split-label">✈️ Away</span>
                        <span className="split-record">
                          <span className="wins-value">{splitRecord.away.w}</span>
                          <span className="split-dash">–</span>
                          <span className="losses-value">{splitRecord.away.l}</span>
                        </span>
                        <span className="split-pct">{winPct(splitRecord.away.w, splitRecord.away.l)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right column */}
            <div className="dashboard-col-right">

              {/* Standings Snippet */}
              {lifecycle?.currentPhase !== "offseason" && (
              <div className="dash-card">
                <div className="dash-card-header">
                  <span className="dash-card-eyebrow">
                    {team?.conference ? `${team.conference} Standings` : "Standings"}
                  </span>
                </div>
                <div className="dash-card-body dash-card-body--tight">
                  {conferenceStandings.length === 0 ? (
                    <p className="dash-empty">No standings data.</p>
                  ) : (
                    <table className="standings-snippet-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Team</th>
                          <th>W</th>
                          <th>L</th>
                          <th>PCT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conferenceStandings.map((t, i) => {
                          const tTheme = getTeamTheme(t.name || t._id);
                          return (
                            <tr key={t._id} className={t.name === team?.name ? "standings-my-team" : ""}>
                              <td className="standings-rank">{i + 1}</td>
                              <td>
                                <span className="standings-team-cell">
                                  <span className="standings-color-dot" style={{ background: tTheme.primary }} />
                                  {t.name}
                                </span>
                              </td>
                              <td>{t.wins || 0}</td>
                              <td>{t.losses || 0}</td>
                              <td>{winPct(t.wins, t.losses)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  <Link to="/standings" className="dash-view-link">View Full Standings →</Link>
                </div>
              </div>
              )}

              {/* Team Leaders */}
              {(teamLeaders.points.length > 0 || teamLeaders.assists.length > 0 || teamLeaders.rebounds.length > 0) && (
                <div className="dash-card">
                  <div className="dash-card-header">
                    <span className="dash-card-eyebrow">Team Leaders</span>
                  </div>
                  <div className="dash-card-body dash-card-body--tight">
                    <div className="leaders-columns">
                      {[
                        { key: "points", label: "PPG", data: teamLeaders.points },
                        { key: "assists", label: "APG", data: teamLeaders.assists },
                        { key: "rebounds", label: "RPG", data: teamLeaders.rebounds },
                      ].map((cat) => {
                        const p = cat.data[0];
                        if (!p) return null;
                        return (
                          <Link
                            key={cat.key}
                            to={`/players/${p.playerId}`}
                            className="team-leader-col"
                          >
                            <div className="leaders-col-header">{cat.label}</div>
                            <div className="team-leader-value">{getLeaderStat(p, cat.key)}</div>
                            <div className="team-leader-name">{p.playerName || "Unknown"}</div>
                            <div className="team-leader-pos">{p.position || ""}</div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* League Leaders */}
              {(leagueLeaders.points.length > 0 || leagueLeaders.assists.length > 0 || leagueLeaders.rebounds.length > 0) && (
                <div className="dash-card">
                  <div className="dash-card-header">
                    <span className="dash-card-eyebrow">League Leaders</span>
                  </div>
                  <div className="dash-card-body dash-card-body--tight">
                    <div className="leaders-columns">
                      {[
                        { key: "points", label: "PPG", data: leagueLeaders.points },
                        { key: "assists", label: "APG", data: leagueLeaders.assists },
                        { key: "rebounds", label: "RPG", data: leagueLeaders.rebounds },
                      ].map((cat) => (
                        <div key={cat.key} className="leaders-col">
                          <div className="leaders-col-header">{cat.label}</div>
                          {cat.data.map((p, i) => {
                            const teamColor = p.teamName ? getTeamTheme(p.teamName).primary : null;
                            return (
                              <Link
                                key={p.playerId || i}
                                to={`/players/${p.playerId}`}
                                className="leaders-row"
                              >
                                <span className="leaders-rank">{i + 1}</span>
                                <span className="leaders-info">
                                  <span className="leaders-name">
                                    {p.playerName || "Unknown"}
                                    {p.position && <span className="leaders-pos">{p.position}</span>}
                                  </span>
                                  {p.teamName && (
                                    <span className="leaders-team">
                                      <span className="leaders-team-dot" style={{ background: teamColor }} />
                                      {p.teamName}
                                    </span>
                                  )}
                                </span>
                                <span className="leaders-value">{getLeaderStat(p, cat.key)}</span>
                              </Link>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    <Link to="/league-leaders" className="dash-view-link">View All Leaders →</Link>
                  </div>
                </div>
              )}

              {/* Recent Results */}
              {lifecycle?.currentPhase !== "offseason" && (
              <div className="dash-card">
                <div className="dash-card-header">
                  <span className="dash-card-eyebrow">Recent Results</span>
                </div>
                <div className="dash-card-body">
                  {recentResults.length === 0 ? (
                    <p className="dash-empty">No games played yet.</p>
                  ) : (
                    <div className="results-list">
                      {recentResults.map((g, i) => {
                        const isHome = g.homeTeam === teamId;
                        const myScore = isHome ? g.homeScore : g.awayScore;
                        const oppScore = isHome ? g.awayScore : g.homeScore;
                        const opponent = isHome ? g.awayTeamName : g.homeTeamName;
                        const won = myScore > oppScore;
                        return (
                          <div key={i} className="result-row">
                            <span className={`result-badge ${won ? "badge-win" : "badge-loss"}`}>{won ? "W" : "L"}</span>
                            <span className="result-opponent">{opponent}</span>
                            <span className="result-score">{myScore} – {oppScore}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {teamId && (
                    <Link to={`/teams/${teamId}/schedule`} className="dash-view-link">View Schedule →</Link>
                  )}
                </div>
              </div>
              )}

              {/* Free Agents - Only during offseason */}
              {lifecycle?.currentPhase === "offseason" && (
                <div className="dash-card">
                  <div className="dash-card-header dash-card-header--flex">
                    <span className="dash-card-eyebrow">Free Agents</span>
                    <span className="fa-available-badge">Available Now</span>
                  </div>
                  <div className="dash-card-body">
                    <p className="fa-dash-desc">
                      Browse unsigned players and add them to your roster.
                    </p>
                    <button
                      className="fa-dash-btn"
                      onClick={() => navigate("/free-agency")}
                    >
                      Browse Free Agents
                    </button>
                  </div>
                </div>
              )}

              {/* Mid-Season Free Agents - Only visible during regular season */}
              {lifecycle?.currentPhase === "regular_season" && (
                <div className="dash-card">
                  <div className="dash-card-header dash-card-header--flex">
                    <span className="dash-card-eyebrow">Mid-Season Signings</span>
                    <span className="mid-season-badge">$1M Contract</span>
                  </div>
                  <div className="dash-card-body">
                    <p className="fa-dash-desc">
                      Sign available free agents for $1M/year. Must have roster space (&lt; 15 players).
                    </p>
                    <button
                      className="mid-season-btn"
                      onClick={() => navigate("/mid-season-free-agents")}
                    >
                      Sign Free Agents
                    </button>
                  </div>
                </div>
              )}

              {/* Strength of Schedule */}
              {lifecycle?.currentPhase !== "offseason" && sos && (
                <div className="dash-card">
                  <div className="dash-card-header">
                    <span className="dash-card-eyebrow">Strength of Schedule</span>
                  </div>
                  <div className="dash-card-body">
                    <div className="sos-main">
                      <span className="sos-pct">{winPct(Math.round(sos.avgPct * 100), Math.round((1 - sos.avgPct) * 100))}</span>
                      <span className="sos-label">Avg Opponent Win %</span>
                    </div>
                    <p className="sos-games">{sos.gamesRemaining} games remaining</p>
                    {sos.hardestName && (
                      <div className="sos-hardest">
                        <span className="sos-hardest-label">Toughest remaining</span>
                        <span className="sos-hardest-team">
                          {sos.hardestName}
                          <span className="sos-hardest-record"> · {sos.hardest.wins}–{sos.hardest.losses}</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;